import { AccordionItem } from "@radix-ui/react-accordion";
import React, { memo, useCallback, useId, useMemo, useState } from "react";

import { cn } from "@/lib/chillnn/cn";

import { useHotelApplicationDataQuery } from "@/components/features/_shared/repository/hotel-application-data/query";

import { Accordion, AccordionContent, AccordionTrigger } from "@/components/ui/accordion/accordion";

import { LockIcon } from "./icon/lock";
import { LockOpenIcon } from "./icon/lock-open";

type Props = {
	hotelID: string;
	accordionTitle: string;
	defaultOpen: boolean;

	children: React.ReactNode;
};

export const AccordionOnlyUseBasic = memo(({ hotelID, accordionTitle, children, defaultOpen = false }: Props) => {
	const uniqueID = useId();
	const { hotelApplicationData } = useHotelApplicationDataQuery(hotelID);

	const canUse = hotelApplicationData?.hotelApplicationStatus.inUseBasic || false;

	const initialOpen = useMemo(() => {
		if (defaultOpen && canUse) {
			return uniqueID;
		}

		return undefined;
	}, [defaultOpen, canUse, uniqueID]);
	const [open, setOpen] = useState(initialOpen);

	const handleOpenChange = useCallback(
		(value: string) => {
			if (value && canUse) {
				setOpen(uniqueID);
			} else {
				setOpen(undefined);
			}
		},
		[canUse, uniqueID],
	);

	return (
		<Accordion type={"single"} collapsible defaultValue={open} onValueChange={handleOpenChange} disabled={!canUse}>
			<AccordionItem value={uniqueID}>
				<AccordionTrigger>
					<div
						className={cn(
							"z-10 flex w-full items-center gap-x-6 [&[data-state=open]>svg]:-rotate-90",
							!canUse && "cursor-not-allowed",
						)}
					>
						<div>{canUse ? <LockOpenIcon /> : <LockIcon />}</div>
						<p>{accordionTitle}</p>
					</div>
				</AccordionTrigger>
				<AccordionContent>
					<div className="flex w-full flex-col gap-y-12 divide-y divide-zinc-100 py-4 [&>*:not(:first-child)]:pt-6">
						{canUse && children}
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
});

AccordionOnlyUseBasic.displayName = "AccordionOnlyUseBasic";
